import React from 'react';
import { Box, Typography, Container, Paper, Link, useTheme } from '@mui/material';
import { GradientText } from '../components/ui/GradientText';
import { ContactForm } from '../components/forms/ContactForm';
import { FAQSection } from '../components/sections/FAQSection';
import { CONTACT_CONTENT } from '../constants';

/**
 * Contact page
 * Contains hero, contact form, and FAQ sections
 */
export const ContactPage: React.FC = () => {
  const theme = useTheme();

  return (
    <Box>
      {/* Hero Section */}
      <Box
        sx={{
          py: { xs: 5, md: 8 },
          background: `linear-gradient(180deg, ${theme.palette.background.default} 0%, ${theme.palette.background.paper} 100%)`,
          textAlign: 'center',
        }}
      >
        <Container maxWidth="md">
          <GradientText
            variant="h1"
            sx={{
              fontWeight: 700,
              mb: 2,
              fontSize: { xs: '2.5rem', md: '3rem' },
            }}
          >
            {CONTACT_CONTENT.hero.title}
          </GradientText>

          <Typography
            variant="h5"
            color="text.secondary"
            sx={{
              fontWeight: 400,
              maxWidth: 600,
              mx: 'auto',
              lineHeight: 1.6,
            }}
          >
            {CONTACT_CONTENT.hero.subtitle}
          </Typography>
        </Container>
      </Box>

      {/* Contact Form Section */}
      <Box sx={{ py: { xs: 4, md: 6 }, bgcolor: 'background.paper' }}>
        <Container maxWidth="md">
          <Paper
            elevation={0}
            sx={{
              p: { xs: 3, md: 5 },
              borderRadius: 3,
              backgroundColor: (theme) =>
                theme.palette.mode === 'light' ? '#FFFFFF' : 'rgba(255, 255, 255, 0.05)',
              boxShadow: '0 4px 24px rgba(0, 0, 0, 0.08)',
            }}
          >
            <Typography
              variant="h4"
              sx={{
                fontWeight: 600,
                mb: 1,
                textAlign: 'center',
              }}
            >
              Send us a Message
            </Typography>

            <Typography
              variant="body1"
              color="text.secondary"
              sx={{
                mb: 4,
                textAlign: 'center',
              }}
            >
              Or email us directly at{' '}
              <Link
                href={`mailto:${CONTACT_CONTENT.info.email}`}
                sx={{
                  color: theme.palette.primary.main,
                  textDecoration: 'none',
                  fontWeight: 500,
                  '&:hover': {
                    textDecoration: 'underline',
                  },
                }}
              >
                {CONTACT_CONTENT.info.email}
              </Link>
            </Typography>

            <ContactForm />
          </Paper>
        </Container>
      </Box>

      {/* FAQ Section */}
      <FAQSection compact />
    </Box>
  );
};
